import type { GuestLintFinding } from '@doss/configbase-tooling/apps'
import type { FrameDiagnosticEntry, FrameDiagnosticLevel } from '@doss/guest-frame'
import { Bug } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '~/@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '~/@/components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from '~/@/components/ui/tooltip'
import { cn } from '~/@/lib/utils'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'

type DebugRow = {
  id: string
  level: FrameDiagnosticLevel
  message: string
  /** Repeats coalesce into a count; rendered only above 1. */
  count?: number
  /** 1-based source line, for findings that carry one. */
  line?: number
}

export type GuestFrameDebugOverlayProps = {
  /** The frame's diagnostic ledger, relayed over the RPC port. */
  entries: readonly FrameDiagnosticEntry[]
  /** Runtime failure the host observed: runtime error, boot timeout, protocol mismatch. */
  frameError: string | undefined
  /** Uncaught guest errors the frame survived, oldest first. */
  runtimeErrors: readonly string[]
  /** Host-side compile failure — the frame never booted, so it has no record of this. */
  compileError: string | undefined
  /** Static authoring-contract findings over the guest source. */
  lintFindings: readonly GuestLintFinding[]
  /** Internal viewers also see info-level entries and source findings. */
  showInternalDetail: boolean
  /** Positioning for the button; the popover and tooltip portal to the body. */
  className?: string
}

const LEVEL_ORDER: Record<FrameDiagnosticLevel, number> = { error: 0, warning: 1, info: 2 }

const LEVEL_CLASS: Record<FrameDiagnosticLevel, { dot: string; icon: string }> = {
  error: { dot: 'bg-red-9', icon: 'text-red-9 hover:text-red-10' },
  warning: { dot: 'bg-yellow-11', icon: 'text-yellow-11 hover:text-yellow-12' },
  info: { dot: 'bg-blue-9', icon: 'text-blue-9 hover:text-blue-10' },
}

const levelOf = (errorCount: number, warningCount: number): FrameDiagnosticLevel => {
  if (errorCount > 0) {
    return 'error'
  }
  return warningCount > 0 ? 'warning' : 'info'
}

/**
 * The one debug affordance for an embed: a level-colored bug icon in the frame's top-right corner
 * opening the full diagnostic list. Host-side, so it renders with ARP tokens, speaks the user's
 * language, and cannot be occluded by guest code.
 *
 * Source findings cap at warning even when the rule's own severity is 'error' — they are authoring
 * contract violations, not a broken embed. Red means something failed at runtime.
 */
export const GuestFrameDebugOverlay = ({
  entries,
  frameError,
  runtimeErrors,
  compileError,
  lintFindings,
  showInternalDetail,
  className,
}: GuestFrameDebugOverlayProps): JSX.Element | null => {
  const { t } = useTypedTranslation()
  const [open, setOpen] = useState(false)

  const runtimeRows = useMemo<DebugRow[]>(() => {
    const hostRows: DebugRow[] = [
      ...(compileError === undefined
        ? []
        : [{ id: 'compile', level: 'error' as const, message: compileError }]),
      ...(frameError === undefined
        ? []
        : [{ id: 'frame', level: 'error' as const, message: frameError }]),
      ...runtimeErrors.map((message, index) => ({
        id: `runtime-${String(index)}`,
        level: 'error' as const,
        message,
      })),
    ]
    const ledgerRows = entries.map((entry) => ({
      id: `entry-${String(entry.seq)}`,
      level: entry.level,
      message: entry.message,
      ...(entry.count > 1 ? { count: entry.count } : {}),
    }))
    return [...hostRows, ...ledgerRows]
      .filter((row) => showInternalDetail || row.level !== 'info')
      .sort((a, b) => LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level])
  }, [compileError, entries, frameError, runtimeErrors, showInternalDetail])

  const sourceRows = useMemo<DebugRow[]>(
    () =>
      showInternalDetail
        ? // Positional: findings are recomputed wholesale from `source` and sorted by line, and
          // rule+line is not unique (one rule can match twice on a line).
          lintFindings.map((finding, index) => ({
            id: `lint-${String(index)}`,
            level: 'warning' as const,
            message: `${finding.rule} — ${finding.message}`,
            line: finding.line,
          }))
        : [],
    [lintFindings, showInternalDetail],
  )

  const rowCount = runtimeRows.length + sourceRows.length
  const errorCount = runtimeRows.filter((row) => row.level === 'error').length
  const warningCount =
    runtimeRows.filter((row) => row.level === 'warning').length + sourceRows.length

  // Auto-open on the first error; the latch keeps a dismissal from being undone by later errors,
  // and resets once everything clears so a fresh failure opens again.
  const autoOpenedRef = useRef(false)
  useEffect(() => {
    if (rowCount === 0) {
      autoOpenedRef.current = false
      setOpen(false)
      return
    }
    if (errorCount > 0 && !autoOpenedRef.current) {
      autoOpenedRef.current = true
      setOpen(true)
    }
  }, [errorCount, rowCount])

  if (rowCount === 0) {
    return null
  }

  const level = levelOf(errorCount, warningCount)
  const counts = [
    ...(warningCount > 0 ? [t('embed.debugWarnings', { count: warningCount })] : []),
    ...(errorCount > 0 ? [t('embed.debugErrors', { count: errorCount })] : []),
  ].join(', ')
  const label =
    counts.length === 0 ? t('embed.showDebug') : t('embed.showDebugWithCounts', { counts })

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                aria-label={label}
                className={cn('bg-white/80 hover:bg-gray-2', LEVEL_CLASS[level].icon, className)}
                data-level={level}
                size="smallIcon"
                variant="ghost"
              >
                <Bug size={14} />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          {/*
           * Portaled because TooltipContent does not portal itself the way PopoverContent does, so
           * the frame's overflow-hidden section would clip it — invisibly to Radix, whose collision
           * detection only measures the viewport. Below-end clears the card's own corner chrome.
           */}
          <TooltipPortal>
            <TooltipContent align="end" side="bottom">
              {label}
            </TooltipContent>
          </TooltipPortal>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent align="end" aria-label={label} className="w-96 p-0">
        <div className="max-h-80 overflow-y-auto p-2" role="log">
          <DebugRowGroup rows={runtimeRows} title={t('embed.debugRuntime')} />
          <DebugRowGroup rows={sourceRows} title={t('embed.debugSource')} />
        </div>
      </PopoverContent>
    </Popover>
  )
}

type DebugRowGroupProps = {
  title: string
  rows: readonly DebugRow[]
}

const DebugRowGroup = ({ title, rows }: DebugRowGroupProps): JSX.Element | null => {
  if (rows.length === 0) {
    return null
  }
  return (
    <section className="mb-1 last:mb-0">
      <h3 className="px-1 py-0.5 text-xs font-semibold uppercase tracking-wide text-gray-9">
        {title}
      </h3>
      <ul className="flex flex-col gap-0.5">
        {rows.map((row) => (
          <li key={row.id} className="flex items-start gap-1.5 rounded px-1 py-0.5 hover:bg-gray-2">
            <span
              aria-hidden
              className={cn('mt-1.5 size-1.5 shrink-0 rounded-full', LEVEL_CLASS[row.level].dot)}
            />
            <span className="min-w-0 grow break-words font-mono text-xs leading-4 text-gray-12">
              {row.line === undefined ? null : (
                <span className="text-gray-9">{`line ${String(row.line)} · `}</span>
              )}
              {row.message}
            </span>
            {row.count === undefined ? null : (
              <span className="mt-0.5 shrink-0 font-mono text-xs text-gray-9">
                {`×${String(row.count)}`}
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}
